import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, CreditCard } from "lucide-react";
import heroImg from "../assets/hero.png";

/**
 * HeroSection — full-viewport hero with the long-exposure vending machine image.
 * Text sits at the bottom-left so the centered machine is not obscured.
 * Nav floats on top (transparent mode).
 *
 * Props:
 *   onCtaClick  — optional callback for the CTA button
 *   ctaLabel    — CTA text (default: "Browse Machines")
 *   variant     — "shop" (default) | "b2b" — adjusts headline + CTA
 *   onBrowse    — optional override for the "Browse Machines" click.
 *                 When not provided, the button scrolls to #market if it
 *                 exists on the page, otherwise navigates to /shop.
 */
export default function HeroSection({ onCtaClick, ctaLabel, variant = "shop", onQuoteClick, onBrowse }) {
  const navigate = useNavigate();

  const isB2B = variant === "b2b";

  const handleCta = () => {
    if (onCtaClick) return onCtaClick();
    if (isB2B) { navigate("/shop"); return; }
    // "Browse Machines" — scroll to #market if present, else navigate to /shop
    if (onBrowse) { onBrowse(); return; }
    const target = document.getElementById("market");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/shop");
    }
  };

  return (
    <section
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        minHeight: 560,
        maxHeight: 900,
        overflow: "hidden",
      }}
    >
      {/* ── Background image ── */}
      <img
        src={heroImg}
        alt="Vending machine in a busy transit station"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
        }}
      />

      {/* ── Gradient overlays for text readability ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          height: "60%",
          background:
            "linear-gradient(to top, rgba(5,5,7,0.88) 0%, rgba(5,5,7,0.5) 50%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "30%",
          background:
            "linear-gradient(to bottom, rgba(5,5,7,0.4) 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* ── Content — bottom-left aligned ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          padding: "0 2rem 48px",
          zIndex: 2,
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ maxWidth: 580 }}>
            {/* Eyebrow */}
            <p
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.6)",
                marginBottom: 12,
              }}
            >
              {isB2B ? "B2B Procurement" : "Smart Vending Solutions"}
            </p>

            {/* Headline */}
            <h1
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
                color: "#ffffff",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                margin: "0 0 16px",
              }}
            >
              {isB2B ? (
                <>
                  Turn Foot Traffic
                  <br />
                  <span style={{ color: "var(--vb-blue)", textShadow: "0 2px 24px rgba(0,0,0,0.85), 0 1px 8px rgba(0,0,0,0.7)" }}>Into Passive Revenue</span>
                </>
              ) : (
                <>
                  A Vending Machine
                  <br />
                  <span style={{ color: "var(--vb-blue)", textShadow: "0 2px 24px rgba(0,0,0,0.85), 0 1px 8px rgba(0,0,0,0.7)" }}>That Pays for Itself</span>
                </>
              )}
            </h1>

            {/* Subheadline */}
            <p
              style={{
                fontFamily: "'Inter', 'Satoshi', sans-serif",
                fontSize: "clamp(14px, 1.6vw, 16px)",
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6,
                margin: "0 0 28px",
                maxWidth: 440,
              }}
            >
              {isB2B
                ? "Volume pricing, dedicated support, and full production oversight — from deposit to delivery."
                : "Estimated ROI in 6–12 months. Cloud-managed, cashless-ready machines for high-traffic locations."}
            </p>

            {/* CTA row */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <button
                onClick={handleCta}
                className="btn-primary"
                style={{ height: 50, padding: "0 32px", fontSize: 14, borderRadius: 12 }}
              >
                {ctaLabel || (isB2B ? "Request a Quote" : "Browse Machines")}
                <ArrowRight style={{ height: 15, width: 15 }} />
              </button>

              {isB2B ? (
                <button
                  onClick={() => navigate("/shop")}
                  className="btn-secondary"
                  style={{ height: 50, padding: "0 28px", fontSize: 14, borderRadius: 12 }}
                >
                  View Catalogue
                </button>
              ) : (
                <button
                  onClick={() => onQuoteClick ? onQuoteClick() : navigate("/")}
                  className="btn-ghost"
                  style={{
                    height: 50, padding: "0 24px", fontSize: 13, borderRadius: 12,
                    borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)",
                  }}
                >
                  Get Business Pricing
                </button>
              )}
            </div>

            {/* Trust bar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px 28px", marginTop: 28 }}>
              {[
                { icon: ShieldCheck, label: "1-Year Warranty" },
                { icon: Truck,       label: "Ships to 40+ Countries" },
                { icon: CreditCard,  label: "Cashless Ready" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon style={{ height: 14, width: 14, color: "var(--vb-blue)", flexShrink: 0 }} />
                  <span
                    style={{
                      fontFamily: "'Inter', 'Satoshi', sans-serif",
                      fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 500,
                    }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div
        style={{
          position: "absolute",
          bottom: 16, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          opacity: 0.4,
          animation: "hero-bounce 2s ease-in-out infinite",
        }}
      >
        <div style={{
          width: 1, height: 24,
          background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.6))",
        }} />
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: "rgba(255,255,255,0.6)",
        }} />
      </div>

      <style>{`
        @keyframes hero-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(6px); }
        }
      `}</style>
    </section>
  );
}
